// Function index:
//   FlatRenderer — Renderer implementation wrapping today's vector/sprite tile
//       culling (./culling.ts) and token lifecycle (./token-registry.ts).
//       Owns its own tileLayer/tokenLayer Containers, inserted into
//       ctx.camera at the correct z-order relative to the roadLayer /
//       overlayLayer / locationLayer hex-scene.ts already added — tiles at
//       the very back, tokens at the very front — so the seam is purely an
//       abstraction: pixel output and event wiring are unchanged from before
//       the extraction.
//
//   getMountedTileGraphics(key) — FlatRenderer-only escape hatch (NOT part of
//       the cross-renderer Renderer interface) returning the live Graphics
//       for a mounted tile, or null. hex-scene.ts uses this to drive the
//       fog/discover reveal-fade tween — a FlatRenderer-specific cosmetic
//       effect tied to Graphics.alpha that doesn't generalise to a future
//       IsoRenderer, so it deliberately isn't in the abstract interface.

import { Container, type Graphics } from 'pixi.js';
import {
  createCullingState, redrawTile, relayout,
  type CullingContext,
} from './culling.js';
import { createTokenRegistry, type TokenRegistry } from './token-registry.js';
import { getTerrainFaceTexture } from './atlas.js';
import type { Renderer, RendererCtx } from './renderer.js';
import type { CharacterInput, NpcInput } from './types.js';

export class FlatRenderer implements Renderer {
  private readonly ctx: RendererCtx;
  private readonly cullCtx: CullingContext;
  private readonly registry: TokenRegistry;
  private readonly tileLayer: Container;
  private readonly tokenLayer: Container;
  private lastHover: { q: number; r: number } | null = null;

  constructor(ctx: RendererCtx) {
    this.ctx = ctx;

    this.tileLayer = new Container();
    // Tiles render beneath whatever hex-scene.ts already added to camera
    // (roads/overlays/locations) — insert at the very back.
    ctx.camera.addChildAt(this.tileLayer, 0);

    this.tokenLayer = new Container();
    // Tokens render above everything else — appending puts it last, i.e. on top.
    ctx.camera.addChild(this.tokenLayer);

    this.cullCtx = {
      state: createCullingState(),
      app: ctx.app,
      camera: ctx.camera,
      tileLayer: this.tileLayer,
      hexData: ctx.hexData,
      discoveredKeys: ctx.discoveredKeys,
      visibilityFor: ctx.visibilityFor,
      // The FACE-cropped texture, not the whole tile canvas: the flat map draws
      // into a hex-sized box, and the canvas carries transparent headroom above
      // the face (for peaks) that would render the face small and off-centre.
      // Self-guards to null when no art is loaded ('plain' style, or interiors),
      // in which case tiles stay on their vector polygons.
      terrainTexture: getTerrainFaceTexture,
      onTileHover: (q, r, key) => {
        this.lastHover = { q, r };
        ctx.onTileHover(q, r, key);
      },
      onTileHoverOut: (key) => { ctx.onTileHoverOut(key); },
      onTileClick: (q, r) => { ctx.onTileClick(q, r); },
      onTileRightClick: (q, r) => { ctx.onTileRightClick(q, r); },
    };

    this.registry = createTokenRegistry(this.tokenLayer);
  }

  relayout(): void {
    relayout(this.cullCtx);
  }

  redrawTile(key: string): void {
    redrawTile(this.cullCtx, key);
  }

  addToken(t: CharacterInput | NpcInput, kind: 'character' | 'npc', _isSelf: boolean): void {
    if (kind === 'character') this.registry.addCharacter(t as CharacterInput);
    else this.registry.addNpc(t as NpcInput);
  }

  moveToken(id: number, kind: 'character' | 'npc', q: number, r: number): void {
    if (kind === 'character') this.registry.moveCharacter(id, q, r, this.ctx.app.ticker);
    else this.registry.moveNpc(id, q, r, this.ctx.app.ticker);
  }

  removeToken(id: number, kind: 'character' | 'npc'): void {
    if (kind === 'character') this.registry.removeCharacter(id);
    else this.registry.removeNpc(id);
  }

  screenToHex(_screenX: number, _screenY: number): { q: number; r: number } | null {
    return this.lastHover;
  }

  destroy(): void {
    // hex-scene.ts's SceneMount.destroy() tears the whole Pixi app down
    // (app.destroy({removeView: true}, {children: true})), which recursively
    // destroys tileLayer/tokenLayer and everything mounted in them. Nothing
    // renderer-owned survives that beyond this call, so there's nothing
    // additional to release here.
  }

  getMountedTileGraphics(key: string): Graphics | null {
    return this.cullCtx.state.mountedTiles.get(key)?.g ?? null;
  }
}
